import { TourProvider, useAlert, useMediaQuery } from "@prismicio/editor-ui"
import { type ReactNode, useEffect, useState } from "react"
import BaseConfetti from "react-confetti"

import {
  trackTourClosed,
  trackTourComplete,
  trackTourStart,
} from "../../analytics/tracking"
import { steps } from "../../steps"
import { GetStartedDialog } from "../GetStartedDialog"
import { MobileBlocker } from "./MobileBlocker"

const CONFETTI_DURATION = 50000

type DemoTourProviderProps = {
  children?: ReactNode
}

export function DemoTourProvider(props: DemoTourProviderProps) {
  const { children } = props

  const [isConfettiRunning, setIsConfettiRunning] = useState(false)
  const [isGetStartedDialogOpen, setIsGetStartedDialogOpen] = useState(false)
  const { showAlert, removeAlert } = useAlert()
  const isMobile = useMediaQuery({ max: "medium" })

  function onStart() {
    trackTourStart()
  }

  function onComplete() {
    setIsConfettiRunning(true)
    setTimeout(() => setIsConfettiRunning(false), CONFETTI_DURATION)

    showAlert({
      id: "tour-complete",
      title: "Congratulations on completing the tour!",
      subtitle:
        "You've just experienced firsthand how Prismic's editing experience can help you create and manage content with ease. Ready to take your content creation experience to the next level?",
      color: "info",
      buttons: {
        action: {
          text: "Get started",
          onClick: () => {
            removeAlert("tour-complete")
            setIsGetStartedDialogOpen(true)
          },
        },
      },
    })

    trackTourComplete()
  }

  function onExit(args: { stepIndex: number | undefined }) {
    const shouldExit = confirm("Are you sure you want to stop the tour?")
    if (shouldExit) trackTourClosed({ stepIndex: args.stepIndex })
    return shouldExit
  }

  return (
    <TourProvider
      steps={steps}
      autoStart={!isMobile}
      autoStartDelay={1000}
      onStart={onStart}
      onComplete={onComplete}
      onExit={onExit}
    >
      {children}
      <MobileBlocker />
      {isConfettiRunning ? <Confetti /> : null}
      <GetStartedDialog
        open={isGetStartedDialogOpen}
        onOpenChange={() => setIsGetStartedDialogOpen(false)}
      />
    </TourProvider>
  )
}

const CONFETTI_COLORS = [
  "hsl(251, 48%, 53%)",
  "hsl(251, 78%, 89%)",
  "hsl(250, 43%, 48%)",
  "hsl(225, 78%, 82%)",
]
const CONFETTI_SIZE = 10

function Confetti() {
  const windowSize = useWindowSize()

  return (
    <BaseConfetti
      width={windowSize.width}
      height={windowSize.height}
      recycle={false}
      gravity={0.2}
      numberOfPieces={2000}
      tweenDuration={CONFETTI_DURATION}
      colors={CONFETTI_COLORS}
      drawShape={drawConfettiShape}
      style={{ zIndex: "auto" }}
    />
  )
}

function drawConfettiShape(ctx: CanvasRenderingContext2D) {
  ctx.fillRect(
    -CONFETTI_SIZE / 2,
    -CONFETTI_SIZE / 2,
    CONFETTI_SIZE,
    CONFETTI_SIZE,
  )
}

function useWindowSize() {
  const [windowSize, setWindowSize] = useState(getWindowSize())

  useEffect(() => {
    const controller = new AbortController()
    window.addEventListener("resize", () => setWindowSize(getWindowSize()), {
      signal: controller.signal,
    })
    return () => controller.abort()
  }, [])

  function getWindowSize() {
    return {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    }
  }

  return windowSize
}
